<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Fornecedores</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Fornecedores</h4>
                    <span class="text-muted fs-7">Exibindo {{ suppliers.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3" @click="confirmDelete(null, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{ selectedIds.length }})
                            </button>
                        </div>
                    </transition>

                    <div class="dropdown me-2 pe-3 border-end">
                        <button class="btn btn-pmg-light dropdown-toggle" type="button" data-bs-toggle="dropdown"
                            aria-expanded="false">
                            <i class="bi bi-printer me-1"></i> Relatórios / Ferramentas
                        </button>
                        <ul class="dropdown-menu shadow border-0 py-2">
                            <li>
                                <h6 class="dropdown-header text-uppercase small fw-bold opacity-75">Exportar Dados</h6>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="exportPdf">
                                    <i class="bi bi-file-earmark-pdf text-danger me-2"></i> Relatório Geral (PDF)
                                </a>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="exportExcel">
                                    <i class="bi bi-file-earmark-excel text-success me-2"></i> Planilha Completa (Excel)
                                </a>
                            </li>
                            <li>
                                <hr class="dropdown-divider opacity-10">
                            </li>
                            <li>
                                <h6 class="dropdown-header text-uppercase small fw-bold opacity-75">Ferramentas</h6>
                            </li>
                            <li>
                                <a class="dropdown-item py-2" href="#" @click.prevent="openImportModal">
                                    <i class="bi bi-cloud-arrow-up text-primary me-2"></i> Importar Fornecedores
                                </a>
                            </li>
                        </ul>
                    </div>
                    <button class="btn btn-pmg-light" @click="refreshData" :disabled="loading" title="Atualizar tela">
                        <i class="bi bi-arrow-clockwise" :class="{ 'spin-animation': loading }"></i>
                    </button>
                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <button class="btn btn-pmg-primary" @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Novo Fornecedor
                    </button>
                </div>
            </div>

            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-6">
                            <input v-model="filter.search" type="text" class="form-control form-control-pmg"
                                placeholder="Pesquisar por razão social, documento, e-mail...">
                        </div>
                        <div class="col-md-4">
                            <select v-model="filter.branch_id" class="form-select form-control-pmg">
                                <option value="">Todas as Empresas</option>
                                <option v-for="branch in branchesList" :key="branch.id" :value="branch.id">
                                    {{ branch.name }}
                                </option>
                            </select>
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Buscar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="table-responsive" style="overflow: visible;">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%" class="text-center">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 35%">Fornecedor</th>
                            <th style="width: 15%">Documento</th>
                            <th style="width: 25%">Contato</th>
                            <th style="width: 10%" class="text-center">Status</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>
                    <tbody v-if="suppliers.length > 0">
                        <tr v-for="item in suppliers" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <td class="text-center">
                                <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <td @click.prevent="openDetails(item)" class="pointer">
                                <div class="fw-bold text-dark">{{ item.name }}</div>
                                <div v-if="item.address && item.address.city" class="small text-muted">
                                    {{ item.address.city }} - {{ item.address.state }}
                                </div>

                                <!-- Badges de Empresas Vinculadas -->
                                <div v-if="item.branches && item.branches.length > 0"
                                    class="d-flex flex-wrap gap-1 mt-1">
                                    <span v-for="branch in item.branches" :key="branch.id"
                                        class="badge bg-light text-secondary border tiny">
                                        <i class="bi bi-building me-1"></i>{{ branch.name }}
                                    </span>
                                </div>
                            </td>
                            <td>{{ item.document }}</td>
                            <td>
                                <div class="pmg-desc-cell">
                                    <div v-if="item.email" class="text-truncate small" style="max-width: 200px;">
                                        <i class="bi bi-envelope me-1"></i> {{ item.email }}
                                    </div>
                                    <div class="d-flex align-items-center gap-2 mt-1">
                                        <i class="bi bi-telephone me-1"></i>
                                        <span class="small">{{ item.phone }}</span>
                                    </div>
                                </div>
                            </td>
                            <td class="text-center">
                                <span :class="{
                                    'badge bg-success-subtle text-success': item.status === 'ativo',
                                    'badge bg-danger-subtle text-danger': item.status === 'bloqueado',
                                    'badge bg-secondary-subtle text-secondary': item.status === 'inativo'
                                }" class="rounded-pill px-3 small fw-bold">
                                    {{ item.status }}
                                </span>
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        aria-expanded="false">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 py-2">
                                        <li><a class="dropdown-item py-2" href="#" @click.prevent="openDetails(item)"><i
                                                    class="bi bi-eye me-2"></i> Detalhes</a></li>
                                        <li><a class="dropdown-item py-2" href="#"
                                                @click.prevent="openModal('edit', item)"><i
                                                    class="bi bi-pencil me-2"></i> Editar</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li><a class="dropdown-item py-2 text-danger" href="#"
                                                @click.prevent="confirmDelete(item, false)"><i
                                                    class="bi bi-trash3 me-2"></i> Excluir</a></li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="6" class="text-center py-5 text-muted">
                                <i class="bi bi-inbox fs-1 mb-2 opacity-50"></i><br>
                                <span>Nenhum fornecedor encontrado.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button class="btn btn-pmg-light px-5 btn-sm fw-bold" @click="loadMore" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <span>{{ loading ? 'A carregar...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <div class="modal fade" id="modalSupplier" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-0">
                        <h4 class="fw-bold m-0 text-dark">{{ isEditing ? 'Editar Fornecedor' : 'Novo Fornecedor' }}</h4>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>

                    <div class="px-4 pt-4">
                        <ul class="nav nav-tabs nav-tabs-pmg" id="supplierTab" role="tablist">
                            <li class="nav-item">
                                <button class="nav-link active" data-bs-toggle="tab"
                                    data-bs-target="#pane-supplier-main" type="button">Dados Principais</button>
                            </li>
                            <li class="nav-item">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#pane-supplier-address"
                                    type="button">Endereço</button>
                            </li>
                            <!-- Nova aba Empresas -->
                            <li class="nav-item">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#pane-supplier-branches"
                                    type="button">Empresas</button>
                            </li>
                            <li class="nav-item">
                                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#pane-supplier-other"
                                    type="button">Outros</button>
                            </li>
                        </ul>
                    </div>

                    <div class="modal-body px-4 py-4">
                        <div class="tab-content">
                            <div class="tab-pane fade show active" id="pane-supplier-main" role="tabpanel">
                                <div class="row g-4">
                                    <div class="col-md-12">
                                        <label class="form-label fw-bold text-secondary small mb-1">Razão Social / Nome
                                            *</label>
                                        <input v-model="form.name" type="text" class="form-control form-control-pmg"
                                            placeholder="Ex: Fornecedor XYZ Ltda">
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group">
                                            <label class="form-label small fw-bold">CNPJ/CPF *</label>
                                            <div class="input-group">
                                                <input type="text" class="form-control"
                                                    :class="{ 'is-invalid': documentError }" v-model="form.document"
                                                    placeholder="Digite apenas números" @blur="handleDocumentBlur"
                                                    maxlength="18">
                                                <button class="btn btn-outline-primary" type="button"
                                                    @click="searchCnpj"
                                                    :disabled="isSearchingCnpj || (form.document?.replace(/\D/g, '').length < 14)">
                                                    <span v-if="isSearchingCnpj"
                                                        class="spinner-border spinner-border-sm"></span>
                                                    <i v-else class="bi bi-search"></i>
                                                </button>
                                            </div>
                                            <div v-if="documentError"
                                                class="invalid-feedback d-block small fw-bold mt-1">
                                                {{ documentError }}
                                            </div>
                                            <small class="text-muted tiny">Digite 14 dígitos para habilitar a consulta
                                                automática.</small>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label fw-bold text-secondary small mb-1">Telefone *</label>
                                        <input v-model="form.phone" @input="applyPhoneMask" type="text"
                                            class="form-control form-control-pmg" placeholder="(00) 00000-0000">
                                    </div>
                                    <div class="col-md-8">
                                        <label class="form-label fw-bold text-secondary small mb-1">E-mail</label>
                                        <input v-model="form.email" type="email" class="form-control form-control-pmg"
                                            placeholder="exemplo@fornecedor.com">
                                    </div>
                                    <div class="col-md-4">
                                        <label class="form-label fw-bold text-secondary small mb-1">Status</label>
                                        <select v-model="form.status" class="form-select form-control-pmg">
                                            <option value="ativo">Ativo</option>
                                            <option value="inativo">Inativo</option>
                                            <option value="bloqueado">Bloqueado</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="tab-pane fade" id="pane-supplier-address" role="tabpanel">
                                <div class="row g-4">
                                    <div class="col-md-4">
                                        <label class="form-label fw-bold text-secondary small mb-1">CEP</label>
                                        <input v-model="form.zip_code" @input="applyCepMask" @blur="fetchAddressByCep"
                                            type="text" class="form-control form-control-pmg" placeholder="00000-000">
                                    </div>
                                    <div class="col-md-8">
                                        <label class="form-label fw-bold text-secondary small mb-1">Logradouro</label>
                                        <input v-model="form.street" type="text" class="form-control form-control-pmg"
                                            placeholder="Rua, Avenida...">
                                    </div>
                                    <div class="col-md-5">
                                        <label class="form-label fw-bold text-secondary small mb-1">Bairro</label>
                                        <input v-model="form.neighborhood" type="text"
                                            class="form-control form-control-pmg" placeholder="Digite o bairro">
                                    </div>
                                    <div class="col-md-5">
                                        <label class="form-label fw-bold text-secondary small mb-1">Cidade</label>
                                        <input v-model="form.city" type="text" class="form-control form-control-pmg"
                                            placeholder="Cidade">
                                    </div>
                                    <div class="col-md-2">
                                        <label class="form-label fw-bold text-secondary small mb-1">UF</label>
                                        <input v-model="form.state" type="text"
                                            class="form-control form-control-pmg text-uppercase" placeholder="UF"
                                            maxlength="2">
                                    </div>
                                </div>
                            </div>
                            <div class="tab-pane fade" id="pane-supplier-branches" role="tabpanel">
                                <div class="mb-4 position-relative">
                                    <label class="form-label fw-bold text-secondary small mb-1">Pesquisar e Vincular
                                        Empresa</label>
                                    <input v-model="searchBranch" @focus="showBranchDropdown = true"
                                        @blur="hideBranchDropdownDelay" type="text"
                                        class="form-control form-control-pmg"
                                        placeholder="Digite o nome da empresa para buscar...">

                                    <!-- Dropdown de Busca Customizado -->
                                    <ul v-if="showBranchDropdown && searchBranch"
                                        class="dropdown-menu show w-100 shadow-sm border mt-1 position-absolute"
                                        style="z-index: 1050; max-height: 200px; overflow-y: auto;">
                                        <li v-if="filteredBranches.length === 0"
                                            class="dropdown-item text-muted small py-2">
                                            Nenhuma empresa encontrada...
                                        </li>
                                        <li v-for="branch in filteredBranches" :key="branch.id">
                                            <a class="dropdown-item py-2 pointer d-flex align-items-center"
                                                @click.prevent="addBranch(branch)">
                                                <i class="bi bi-building me-2 text-primary"></i> {{ branch.name }}
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <!-- Empty State -->
                                <div v-if="form.branch_ids.length === 0"
                                    class="p-5 border-dashed rounded-3 text-center mt-3 bg-light-subtle">
                                    <i class="bi bi-building fs-1 text-secondary opacity-50 mb-2 d-block"></i>
                                    <p class="mb-0 small text-muted">Nenhuma empresa vinculada a este fornecedor no
                                        momento.</p>
                                </div>

                                <!-- Lista de Empresas Selecionadas -->
                                <div v-else class="mt-3">
                                    <h6 class="small fw-bold text-muted mb-3">Empresas Vinculadas ({{
                                        form.branch_ids.length }})</h6>
                                    <div class="row g-2">
                                        <div v-for="branch in selectedBranchesData" :key="branch.id" class="col-md-6">
                                            <div
                                                class="d-flex align-items-center justify-content-between p-2 border rounded bg-white shadow-sm">
                                                <div class="d-flex align-items-center overflow-hidden">
                                                    <i class="bi bi-building fs-5 text-secondary me-2"></i>
                                                    <div class="fw-bold small text-dark text-truncate">{{ branch.name }}
                                                    </div>
                                                </div>
                                                <button type="button" @click="removeBranch(branch.id)"
                                                    class="btn btn-sm text-danger ms-2 p-1" title="Remover vínculo">
                                                    <i class="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="tab-pane fade" id="pane-supplier-other" role="tabpanel">
                                <label class="form-label fw-bold text-secondary small mb-1">Observações Internas</label>
                                <textarea v-model="form.notes" class="form-control form-control-pmg" rows="5"
                                    placeholder="Digite detalhes relevantes..."></textarea>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isFormValid" @click="saveSupplier">
                            {{ isEditing ? 'Atualizar' : 'Salvar Fornecedor' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h5 class="fw-bold">Tem a certeza?</h5>
                        <p class="text-muted small">{{ isBulkDelete ? `Excluir ${selectedIds.length} fornecedor(es)
                            selecionado(s)?` : `Excluir "${itemToDelete?.name}"?` }}</p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="modalSupplierDetails" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-0">
                        <h4 class="fw-bold m-0 text-dark">Detalhes do Fornecedor</h4>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>

                    <div class="modal-body px-4 py-4" v-if="selectedSupplier">
                        <h6 class="text-uppercase text-muted fw-bold small mb-3 letter-spacing-1">Informações Gerais
                        </h6>
                        <div class="row g-3 mb-4">
                            <div class="col-md-7">
                                <p class="text-muted small mb-1">Razão Social / Nome</p>
                                <p class="fw-bold fs-5 mb-0 text-dark">{{ selectedSupplier.name }}</p>
                            </div>
                            <div class="col-md-5">
                                <p class="text-muted small mb-1">Documento (CPF/CNPJ)</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.document }}</p>
                            </div>
                            <div class="col-md-4 mt-3">
                                <p class="text-muted small mb-1">Telefone</p>
                                <p class="fw-bold mb-0 text-success">{{ selectedSupplier.phone }}</p>
                            </div>
                            <div class="col-md-8 mt-3">
                                <p class="text-muted small mb-1">E-mail</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.email || 'Não informado' }}</p>
                            </div>
                        </div>

                        <hr class="opacity-10 my-4">

                        <h6 class="text-uppercase text-muted fw-bold small mb-3 letter-spacing-1">Endereço Completo</h6>
                        <div class="row g-3 mb-4">
                            <div class="col-md-9">
                                <p class="text-muted small mb-1">Logradouro</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.address?.street || '-' }}</p>
                            </div>
                            <div class="col-md-3">
                                <p class="text-muted small mb-1">CEP</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.address?.zip_code || '-' }}</p>
                            </div>
                            <div class="col-md-5 mt-3">
                                <p class="text-muted small mb-1">Bairro</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.address?.neighborhood || '-' }}
                                </p>
                            </div>
                            <div class="col-md-4 mt-3">
                                <p class="text-muted small mb-1">Cidade</p>
                                <p class="fw-bold mb-0 text-dark">{{ selectedSupplier.address?.city || '-' }}</p>
                            </div>
                            <div class="col-md-3 mt-3">
                                <p class="text-muted small mb-1">Estado (UF)</p>
                                <p class="fw-bold mb-0 text-dark text-uppercase">{{ selectedSupplier.address?.state ||
                                    '-' }}</p>
                            </div>
                        </div>

                        <h6 class="text-uppercase text-muted fw-bold small mb-2 letter-spacing-1 mt-4">Observações</h6>
                        <div class="bg-light rounded-3 p-3 border shadow-sm">
                            <p class="mb-0 text-secondary small" style="white-space: pre-line;">
                                {{ selectedSupplier.notes || 'Nenhuma observação registada.' }}
                            </p>
                        </div>
                        <h6 class="text-uppercase text-muted fw-bold small mb-2 letter-spacing-1 mt-4">Empresas
                            Vinculadas</h6>
                        <div v-if="selectedSupplier.branches && selectedSupplier.branches.length > 0"
                            class="row g-2 mb-4">
                            <div v-for="branch in selectedSupplier.branches" :key="branch.id" class="col-md-6">
                                <div class="d-flex align-items-center p-2 border rounded bg-light shadow-sm">
                                    <i class="bi bi-building fs-5 text-secondary me-2"></i>
                                    <div>
                                        <div class="fw-bold small text-dark">{{ branch.name }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-muted small mb-4">
                            Nenhuma empresa específica vinculada a este fornecedor.
                        </div>
                    </div>
                    <div class="modal-footer border-0 p-4 pt-2">
                        <button class="btn btn-outline-light w-100 rounded-3 py-2 fw-medium text-muted border shadow-sm"
                            data-bs-dismiss="modal">
                            <i class="bi bi-x me-1"></i> Fechar Detalhes
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="modalImportSupplierPreview" tabindex="-1" aria-hidden="true"
            ref="modalImportSupplierPreview">
            <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4">
                        <h5 class="fw-bold m-0">
                            <i class="bi bi-file-earmark-arrow-up me-2 text-primary"></i>
                            Pré-visualização da Importação
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetImport"></button>
                    </div>

                    <div class="modal-body p-4">
                        <div class="d-flex justify-content-between align-items-center mb-3 ">
                            <span class="text-muted small">Siga o padrão das colunas para evitar erros.</span>
                            <a href="#" @click.prevent="downloadTemplate"
                                class="btn btn-link btn-sm p-0 text-decoration-none">
                                <i class="bi bi-download me-1"></i> Baixar Planilha Exemplo
                            </a>
                        </div>

                        <div v-if="importDuplicates.length > 0" class="alert alert-danger border-0 shadow-sm mb-3">
                            <div class="d-flex align-items-center">
                                <i class="bi bi-layers-half fs-4 me-3"></i>
                                <div>
                                    <h6 class="fw-bold mb-1">Dados Duplicados na Planilha</h6>
                                    <p class="small mb-0">
                                        Encontramos registros com o mesmo CNPJ/CPF ou E-mail nas linhas:
                                        <strong>{{importDuplicates.map(d => d.linha).join(', ')}}</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="importErrors.length > 0" class="alert alert-warning border-0 shadow-sm mb-3">
                            <div class="d-flex">
                                <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                                <div>
                                    <h6 class="fw-bold mb-1">Atenção: {{ importErrors.length }} linha(s) com dados
                                        incompletos</h6>
                                    <p class="small mb-0 text-secondary">
                                        As seguintes linhas no Excel precisam de correção:
                                        <strong>{{importErrors.map(e => e.linha).join(', ')}}</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div v-if="!importPreviewData.length"
                            class="upload-area p-5 border-2 border-dashed rounded text-center bg-light dropzone-box">
                            <input type="file" ref="fileInputImport" @change="handleFilePreview" class="d-none"
                                accept=".xlsx,.csv">
                            <i class="bi bi-file-earmark-excel fs-1 text-muted opacity-50"></i>
                            <p class="mt-3 mb-4">Selecione a planilha de fornecedores para análise</p>
                            <button class="btn btn-pmg-primary" @click="$refs.fileInputImport.click()">
                                Escolher Arquivo
                            </button>
                        </div>

                        <div v-else>
                            <div class="alert alert-info border-0 rounded-3 small">
                                <i class="bi bi-info-circle me-2"></i>
                                Encontramos <strong>{{ importPreviewData.length }}</strong> registros. Verifique se as
                                colunas estão corretas antes de confirmar.
                            </div>

                            <div class="table-responsive rounded-3 border">
                                <table class="table table-sm table-hover mb-0" style="min-width: 1000px;">
                                    <thead class="bg-light">
                                        <tr>
                                            <th class="small text-muted py-2 ps-3" style="width: 50px;">#</th>
                                            <th class="small text-muted py-2" style="width: 220px;">RAZÃO SOCIAL / NOME
                                            </th>
                                            <th class="small text-muted py-2" style="width: 140px;">DOCUMENTO</th>
                                            <th class="small text-muted py-2">E-MAIL</th>
                                            <th class="small text-muted py-2" style="width: 130px;">TELEFONE</th>
                                            <th class="small text-muted py-2" style="width: 130px;">CIDADE/UF</th>
                                            <th class="small text-muted py-2 text-center" style="width: 100px;">STATUS
                                            </th>
                                            <th class="small text-muted py-2" style="width: 180px;">OBSERVAÇÕES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, index) in importPreviewData" :key="index"
                                            :class="{ 'table-danger-light': row.isInvalid }">
                                            <td class="ps-3 small text-muted">{{ row.excelRow }}</td>
                                            <td class="small fw-bold">
                                                <div class="text-truncate" style="max-width: 210px;" :title="row.name">
                                                    <i v-if="row.isInvalid"
                                                        class="bi bi-exclamation-circle-fill text-danger me-1"></i>
                                                    {{ row.name }}
                                                </div>
                                            </td>
                                            <td class="small">{{ row.document }}</td>
                                            <td class="small text-muted">
                                                <div class="text-truncate" style="max-width: 180px;" :title="row.email">
                                                    {{ row.email || '-' }}
                                                </div>
                                            </td>
                                            <td class="small">{{ row.phone || '-' }}</td>
                                            <td class="small">
                                                <template v-if="row.city || row.state">
                                                    {{ row.city }}{{ row.state ? '/' + row.state : '' }}
                                                </template>
                                                <template v-else>-</template>
                                            </td>
                                            <td class="small text-center">
                                                <span class="badge rounded-pill px-2" :class="{
                                                    'bg-success-subtle text-success': row.status === 'ativo',
                                                    'bg-danger-subtle text-danger': row.status === 'bloqueado',
                                                    'bg-secondary-subtle text-secondary': row.status === 'inativo'
                                                }">
                                                    {{ row.status || 'ativo' }}
                                                </span>
                                            </td>
                                            <td class="small text-muted">
                                                <div class="text-truncate" style="max-width: 170px;" :title="row.notes">
                                                    {{ row.notes || '-' }}
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer border-0 p-4 pt-0">
                        <button class="btn btn-pmg-light" data-bs-dismiss="modal" @click="resetImport">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!importPreviewData.length || loading"
                            @click="executeFinalImport">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                            Confirmar e Importar Fornecedores
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import * as XLSX from 'xlsx';

export default {
    inject: ['globalLoading'],
    data() {
        return {
            suppliers: [], alerts: [], loading: false, isEditing: false, showFilter: false,
            filter: { search: '', branch_id: '' }, selectedIds: [], totalItems: 0, currentPage: 1, lastPage: 1, hasMore: false,
            form: {
                id: null, name: '', document: '', email: '', phone: '', status: 'ativo',
                zip_code: '', street: '', neighborhood: '', city: '', state: '', notes: '',
                branch_ids: []
            },
            modalInstance: null, deleteModalInstance: null, detailsModalInstance: null,
            itemToDelete: null, isBulkDelete: false, selectedSupplier: null,
            searchingCnpj: false,
            isSearchingCnpj: false,
            selectedFile: null,
            isDragging: false,
            importPreviewData: [],
            importDuplicates: [],
            importErrors: [],
            selectedImportFile: null,
            importModalInstance: null,
            documentError: null,
            branchesList: [],
            searchBranch: '',
            showBranchDropdown: false,
        }
    },
    computed: {
        isAllSelected() { return this.suppliers.length > 0 && this.selectedIds.length === this.suppliers.length; },
        isFormValid() { return this.form.name && this.form.document && this.form.phone; },
        filteredBranches() {
            if (!this.searchBranch) return [];
            const term = this.searchBranch.toLowerCase();
            return this.branchesList.filter(b =>
                b.name.toLowerCase().includes(term) && !this.form.branch_ids.includes(b.id)
            );
        },
        selectedBranchesData() {
            return this.form.branch_ids
                .map(id => this.branchesList.find(b => b.id === id))
                .filter(Boolean);
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.initModals();
        });
        this.fetchSuppliers();
        this.fetchBranchesList();
    },
    watch: {
        'form.document'(newVal) {
            if (!newVal) {
                this.documentError = null;
                return;
            }

            let rawValue = newVal.replace(/\D/g, "");
            let maskedValue = rawValue;

            if (rawValue.length <= 11) {
                maskedValue = maskedValue.replace(/(\d{3})(\d)/, "$1.$2");
                maskedValue = maskedValue.replace(/(\d{3})(\d)/, "$1.$2");
                maskedValue = maskedValue.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
            } else {
                maskedValue = maskedValue.replace(/^(\d{2})(\d)/, "$1.$2");
                maskedValue = maskedValue.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
                maskedValue = maskedValue.replace(/\.(\d{3})(\d)/, ".$1/$2");
                maskedValue = maskedValue.replace(/(\d{4})(\d)/, "$1-$2");
            }

            if (newVal !== maskedValue) {
                this.form.document = maskedValue;
                return;
            }

            if (rawValue.length === 11) {
                this.documentError = this.validateCPF(rawValue) ? null : 'CPF inválido.';
            } else if (rawValue.length === 14) {
                this.documentError = this.validateCNPJ(rawValue) ? null : 'CNPJ inválido.';
            } else if (rawValue.length > 11 && rawValue.length < 14) {
                this.documentError = 'Documento incompleto (CNPJ exige 14 números).';
            } else if (rawValue.length > 14) {
                this.documentError = 'CNPJ inválido.';
            } else {
                this.documentError = null;
            }
        }
    },
    methods: {
        initModals() {
            const modalEl = document.getElementById('modalSupplier');
            const detailsEl = document.getElementById('modalSupplierDetails');
            const deleteEl = document.getElementById('confirmDeleteModal');
            const importEl = this.$refs.modalImportSupplierPreview || document.getElementById('modalImportSupplierPreview');

            if (modalEl) this.modalInstance = new window.bootstrap.Modal(modalEl);
            if (detailsEl) this.detailsModalInstance = new window.bootstrap.Modal(detailsEl);
            if (deleteEl) this.deleteModalInstance = new window.bootstrap.Modal(deleteEl);
            if (importEl) this.importModalInstance = new window.bootstrap.Modal(importEl);
        },

        async fetchSuppliers(page = 1, append = false) {
            if (this.globalLoading) this.globalLoading.show('A carregar fornecedores...');
            this.loading = true;
            try {
                const res = await api.get('/suppliers', { params: { page, search: this.filter.search, branch_id: this.filter.branch_id } });
                const { data, meta } = res.data;
                this.suppliers = append ? [...this.suppliers, ...data] : data;
                this.totalItems = meta.total;
                this.currentPage = meta.current_page;
                this.lastPage = meta.last_page;
                this.hasMore = this.currentPage < this.lastPage;
            } catch (e) {
                this.showAlert("Erro ao buscar dados.", "error");
            } finally {
                this.loading = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        },
        async fetchBranchesList() {
            try {
                const res = await api.get('/branches', { params: { per_page: 100 } });
                this.branchesList = res.data.data || res.data;
            } catch (e) {
                console.error("Erro ao carregar Empresas para o filtro", e);
            }
        },

        openModal(mode, item = null) {
            this.searchBranch = '';
            this.showBranchDropdown = false;
            this.isEditing = (mode === 'edit');

            if (this.isEditing && item) {
                this.form = { ...item };
                this.form.branch_ids = item.branches ? item.branches.map(b => b.id) : [];

                if (item.address) {
                    this.form.zip_code = item.address.zip_code;
                    this.form.street = item.address.street;
                    this.form.neighborhood = item.address.neighborhood;
                    this.form.city = item.address.city;
                    this.form.state = item.address.state;
                }
            } else {
                this.form = {
                    id: null, name: '', document: '', email: '', phone: '', status: 'ativo',
                    zip_code: '', street: '', neighborhood: '', city: '', state: '', notes: '',
                    branch_ids: []
                };
            }

            if (this.modalInstance) this.modalInstance.show();
        },

        async saveSupplier() {
            if (!this.isFormValid) return;
            this.loading = true;

            try {
                if (this.globalLoading) this.globalLoading.show('A guardar alterações...');

                const payload = {
                    name: this.form.name,
                    document: this.form.document,
                    email: this.form.email,
                    phone: this.form.phone,
                    notes: this.form.notes || '',
                    zip_code: this.form.zip_code,
                    street: this.form.street,
                    neighborhood: this.form.neighborhood,
                    city: this.form.city,
                    state: this.form.state,
                    status: this.form.status,
                    branch_ids: this.form.branch_ids
                };

                const url = this.isEditing ? `/suppliers/${this.form.id}` : '/suppliers';
                const method = this.isEditing ? 'put' : 'post';

                await api[method](url, payload);

                this.showAlert(this.isEditing ? "Fornecedor atualizado!" : "Fornecedor cadastrado!");

                if (this.modalInstance) this.modalInstance.hide();

                this.fetchSuppliers(1, false);

            } catch (e) {
                if (e.response?.status === 403) {
                    this.showAlert("Permissão de acesso ao recurso não autorizada.", "error");
                } else if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao salvar.", "error");
                }
            } finally {
                this.loading = false;
                if (this.globalLoading) this.globalLoading.hide();
            }
        },

        confirmDelete(target, isBulk = false) {
            this.itemToDelete = target;
            this.isBulkDelete = isBulk;
            if (this.deleteModalInstance) this.deleteModalInstance.show();
        },

        async executeDeletion() {
            try {
                if (this.isBulkDelete) await api.post('/suppliers/bulk-delete', { ids: this.selectedIds });
                else await api.delete(`/suppliers/${this.itemToDelete.id}`);

                this.showAlert("Excluído com sucesso!", "danger");
                this.selectedIds = [];
                this.fetchSuppliers(1, false);
            } catch (e) {
                this.showAlert("Erro ao excluir.", "error");
            } finally {
                if (this.deleteModalInstance) this.deleteModalInstance.hide();
            }
        },

        openDetails(item) {
            this.selectedSupplier = item;
            if (this.detailsModalInstance) {
                this.detailsModalInstance.show();
            }
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            const icons = { success: 'bi-check-circle-fill', danger: 'bi-exclamation-circle-fill', error: 'bi-x-circle-fill' };
            this.alerts.push({ id, message, type, icon: icons[type] || icons.success });
            setTimeout(() => this.removeAlert(id), 8000);
        },
        removeAlert(id) { this.alerts = this.alerts.filter(a => a.id !== id); },

        async fetchAddressByCep() {
            const cep = this.form.zip_code.replace(/\D/g, '');
            if (cep.length === 8) {
                try {
                    const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                    const d = await r.json();
                    if (!d.erro) {
                        this.form.street = d.logradouro;
                        this.form.neighborhood = d.bairro;
                        this.form.city = d.localidade;
                        this.form.state = d.uf;
                    }
                } catch (e) { console.error(e); }
            }
        },

        applyDocumentMask(e) {
            let v = e.target.value.replace(/\D/g, "");
            if (v.length <= 11) {
                v = v.replace(/(\d{3})(\d)/, "$1.$2");
                v = v.replace(/(\d{3})(\d)/, "$1.$2");
                v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
            } else {
                v = v.replace(/^(\d{2})(\d)/, "$1.$2");
                v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
                v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
                v = v.replace(/(\d{4})(\d)/, "$1-$2");
            }
            this.form.document = v;
        },
        applyPhoneMask(e) {
            let v = e.target.value.replace(/\D/g, "");

            if (v.length > 11) {
                v = v.substring(0, 11);
            }

            if (v.startsWith("0800") || v.startsWith("0300")) {
                if (v.length > 7) {
                    v = v.replace(/^(\d{4})(\d{3})(\d{0,4}).*/, "$1 $2 $3");
                } else if (v.length > 4) {
                    v = v.replace(/^(\d{4})(\d{0,3})/, "$1 $2");
                }
            } else {
                if (v.length > 10) {
                    v = v.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
                } else if (v.length > 6) {
                    v = v.replace(/^(\d{2})(\d{4})(\d+)/, "($1) $2-$3");
                } else if (v.length > 2) {
                    v = v.replace(/^(\d{2})(\d+)/, "($1) $2");
                } else if (v.length > 0) {
                    v = v.replace(/^(\d*)/, "($1");
                }
            }

            this.form.phone = v;
        },

        applyCepMask() {
            this.form.zip_code = this.form.zip_code.replace(/\D/g, '').replace(/^(\d{5})(\d)/, "$1-$2");
        },

        toggleAll(e) { this.selectedIds = e.target.checked ? this.suppliers.map(s => s.id) : []; },
        applyFilter() { this.showFilter = false; this.fetchSuppliers(1, false); },
        loadMore() { if (this.hasMore) this.fetchSuppliers(this.currentPage + 1, true); },
        async exportPdf() {
            if (this.globalLoading) this.globalLoading.show('A gerar relatório PDF...');
            try {
                const res = await api.get('/suppliers/export/pdf', {
                    params: this.filter,
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_fornecedores.pdf');
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (e) {
                this.showAlert("Erro ao gerar PDF.", "error");
            } finally {
                if (this.globalLoading) this.globalLoading.hide();
            }
        },

        async exportExcel() {
            if (this.globalLoading) this.globalLoading.show('A gerar planilha Excel...');
            try {
                const res = await api.get('/suppliers/export/excel', {
                    params: this.filter,
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'fornecedores.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (e) {
                this.showAlert("Erro ao gerar Excel.", "error");
            } finally {
                if (this.globalLoading) this.globalLoading.hide();
            }
        },

        validateCPF(cpf) {
            cpf = cpf.replace(/[^\d]+/g, '');
            if (cpf === '') return false;

            if (cpf.length !== 11 ||
                cpf === "00000000000" || cpf === "11111111111" ||
                cpf === "22222222222" || cpf === "33333333333" ||
                cpf === "44444444444" || cpf === "55555555555" ||
                cpf === "66666666666" || cpf === "77777777777" ||
                cpf === "88888888888" || cpf === "99999999999") {
                return false;
            }

            let add = 0;
            for (let i = 0; i < 9; i++) {
                add += parseInt(cpf.charAt(i)) * (10 - i);
            }
            let rev = 11 - (add % 11);
            if (rev === 10 || rev === 11) rev = 0;
            if (rev !== parseInt(cpf.charAt(9))) return false;

            add = 0;
            for (let i = 0; i < 10; i++) {
                add += parseInt(cpf.charAt(i)) * (11 - i);
            }
            rev = 11 - (add % 11);
            if (rev === 10 || rev === 11) rev = 0;
            if (rev !== parseInt(cpf.charAt(10))) return false;

            return true;
        },

        validateCNPJ(cnpj) {
            if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
            let size = cnpj.length - 2;
            let numbers = cnpj.substring(0, size);
            let digits = cnpj.substring(size);
            let sum = 0;
            let pos = size - 7;
            for (let i = size; i >= 1; i--) {
                sum += parseInt(numbers.charAt(size - i)) * pos--;
                if (pos < 2) pos = 9;
            }
            let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            if (result !== parseInt(digits.charAt(0))) return false;
            size = size + 1;
            numbers = cnpj.substring(0, size);
            sum = 0;
            pos = size - 7;
            for (let i = size; i >= 1; i--) {
                sum += parseInt(numbers.charAt(size - i)) * pos--;
                if (pos < 2) pos = 9;
            }
            result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            return result === parseInt(digits.charAt(1));
        },
        handleDocumentBlur() {
            const rawValue = this.form.document ? this.form.document.replace(/\D/g, '') : '';
            if (rawValue === '') {
                this.documentError = null;
                return;
            }

            if (rawValue.length <= 11) {
                this.documentError = this.validateCPF(rawValue) ? null : 'CPF inválido.';
            } else {
                this.documentError = this.validateCNPJ(rawValue) ? null : 'CNPJ inválido.';
            }
        },
        async searchCnpj() {
            this.globalLoading.show('Carregando dados...');
            const cnpjClear = this.form.document ? this.form.document.replace(/\D/g, '') : '';

            if (cnpjClear.length !== 14) return;

            this.isSearchingCnpj = true;

            try {
                const response = await api.get(`/external/cnpj/${cnpjClear}`);
                const data = response.data;

                this.form.name = data.razao_social || data.nome_fantasia || this.form.name;
                this.form.email = data.email || this.form.email;

                if (data.telefone) {
                    let v = data.telefone.replace(/\D/g, "");
                    if (v.length > 11) v = v.substring(0, 11);
                    if (v.length > 10) v = v.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
                    else if (v.length > 6) v = v.replace(/^(\d{2})(\d{4})(\d+)/, "($1) $2-$3");
                    else if (v.length > 2) v = v.replace(/^(\d{2})(\d+)/, "($1) $2");
                    else if (v.length > 0) v = v.replace(/^(\d*)/, "($1");
                    this.form.phone = v;
                }

                if (data.cep) {
                    this.form.zip_code = data.cep.replace(/\D/g, '').replace(/^(\d{5})(\d)/, "$1-$2");
                }

                this.form.street = data.logradouro || this.form.street;
                this.form.neighborhood = data.bairro || this.form.neighborhood;
                this.form.city = data.cidade || this.form.city;
                this.form.state = data.uf || this.form.state;

            } catch (e) {
                console.error("Erro ao buscar CNPJ:", e);
            } finally {
                this.isSearchingCnpj = false;
                this.globalLoading.hide();
            }
        },
        openImportModal() {
            this.resetImport();
            if (this.importModalInstance) {
                this.importModalInstance.show();
            }
        },
        resetImport() {
            this.importPreviewData = [];
            this.importDuplicates = [];
            this.importErrors = [];
            this.selectedImportFile = null;
            if (this.$refs.fileInputImport) {
                this.$refs.fileInputImport.value = '';
            }
        },

        handleFilePreview(event) {
            const file = event.target.files[0];
            if (!file) return;

            this.selectedImportFile = file;
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const rawJson = XLSX.utils.sheet_to_json(worksheet);

                const docCounts = new Map();
                const emailCounts = new Map();

                // 1. Mapeamento para checagem de duplicidades (adicionado 'document')
                rawJson.forEach(row => {
                    const doc = row['CPF_CNPJ'] || row['documento'] || row['Documento'] || row['CNPJ'] || row['CPF'] || row['document'];
                    const email = row['E-mail'] || row['email'] || row['Email'];
                    if (doc) docCounts.set(doc, (docCounts.get(doc) || 0) + 1);
                    if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
                });

                const errors = [];
                const duplicates = [];

                // 2. Extração de dados da linha (adicionadas as chaves em inglês do seu arquivo)
                this.importPreviewData = rawJson.map((row, index) => {
                    const excelRow = index + 2;

                    const rowData = {
                        excelRow: excelRow,
                        name: row['Razão Social'] || row['Nome'] || row['nome'] || row['Razão_Social'] || row['name'] || '---',
                        document: row['CPF_CNPJ'] || row['documento'] || row['Documento'] || row['CNPJ'] || row['CPF'] || row['document'] || '---',
                        email: row['E-mail'] || row['email'] || row['Email'] || '---',
                        phone: row['Telefone'] || row['telefone'] || row['phone'] || '---',
                        city: row['Cidade'] || row['cidade'] || row['city'] || '---',
                        state: row['UF'] || row['uf'] || row['state'] || '--',
                        status: row['Status'] || row['status'] || 'ativo',
                        notes: row['Observações'] || row['observacoes'] || row['Notas'] || row['notes'] || '-'
                    };

                    const missingFields = [];
                    const duplicateFields = [];

                    if (rowData.name === '---') missingFields.push('Nome/Razão Social');
                    if (rowData.document === '---') missingFields.push('Documento');

                    if (rowData.document !== '---' && docCounts.get(rowData.document) > 1) duplicateFields.push('Doc Repetido');
                    if (rowData.email !== '---' && emailCounts.get(rowData.email) > 1) duplicateFields.push('E-mail Repetido');

                    if (missingFields.length > 0) {
                        errors.push({ linha: excelRow, campos: missingFields.join(', ') });
                    }
                    if (duplicateFields.length > 0) {
                        duplicates.push({ linha: excelRow, info: duplicateFields.join(' e ') });
                    }

                    rowData.isInvalid = missingFields.length > 0;
                    rowData.isDuplicate = duplicateFields.length > 0;
                    rowData.errorsList = [...missingFields, ...duplicateFields].join(', ');

                    return rowData;
                });

                this.importErrors = errors;
                this.importDuplicates = duplicates;
            };
            reader.readAsArrayBuffer(file);
        },

        async executeFinalImport() {
            if (!this.selectedImportFile) return;

            this.loading = true;
            const fd = new FormData();
            fd.append('file', this.selectedImportFile);

            try {
                await api.post('/suppliers/import', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                this.showAlert("Fornecedores importados com sucesso!");

                // Correção aqui: a variável declarada no data() é importModalInstance
                if (this.importModalInstance) {
                    this.importModalInstance.hide();
                }

                this.fetchSuppliers(1, false);
            } catch (e) {
                // Exibe o erro real no console para ajudar em futuros diagnósticos
                console.error("Erro na importação:", e);
                this.showAlert("Erro ao processar arquivo no servidor.", "error");
            } finally {
                this.loading = false;
            }
        },

        downloadTemplate() {
            const link = document.createElement('a');
            link.href = '/templates/modelo_importacao_fornecedores.xlsx';
            link.setAttribute('download', 'modelo_importacao_fornecedores.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        },
        addBranch(branch) {
            if (!this.form.branch_ids.includes(branch.id)) {
                this.form.branch_ids.push(branch.id);
            }
            this.searchBranch = ''; // Limpa a busca
            this.showBranchDropdown = false; // Fecha o dropdown
        },
        removeBranch(id) {
            this.form.branch_ids = this.form.branch_ids.filter(bId => bId !== id);
        },
        hideBranchDropdownDelay() {
            // Pequeno atraso para dar tempo de registrar o clique no item da lista antes de esconder a <ul>
            setTimeout(() => {
                this.showBranchDropdown = false;
            }, 200);
        },
        refreshData() {
            this.fetchSuppliers(1, false);
        },
    }
}
</script>

<style scoped>
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-weight: 600;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    padding: 10px 20px;
}

.form-control-pmg {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    padding: 10px 14px;
}

.nav-tabs-pmg .nav-link.active {
    color: #561569;
    border-bottom: 3px solid #561569;
    background: transparent;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.pointer {
    cursor: pointer;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.nav-tabs-pmg {
    border-bottom: 1px solid #e2e8f0;
    gap: 2rem;
    border-top: none;
    border-left: none;
    border-right: none;
}

.nav-tabs-pmg .nav-link {
    border: none !important;
    color: #94a3b8 !important;
    font-weight: 600;
    padding: 0.5rem 0;
    background: transparent !important;
    font-size: 0.95rem;
    transition: color 0.2s ease;
}

.nav-tabs-pmg .nav-link.active {
    color: #561569 !important;
    border-bottom: 3px solid #561569 !important;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.bg-secondary-subtle {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
    font-size: 0.65rem !important;
    letter-spacing: 0.5px;
}

.bg-pmg-light {
    background-color: rgba(13, 110, 253, 0.1);
}

.bg-success-light {
    background-color: rgba(25, 135, 84, 0.1);
}

.bg-danger-light {
    background-color: rgba(220, 53, 69, 0.1);
}

.dropdown-item {
    font-size: 0.9rem;
    color: #334155;
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    background-color: #f8fafc;
    color: #1e293b;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.dropzone-box {
    border: 2px dashed #cbd5e1;
    background-color: #fafafa;
    min-height: 260px;
    transition: all 0.2s ease-in-out;
}

.dropzone-box.dragging {
    border-color: #561569;
    background-color: #f3e8f4;
}

.tiny {
    font-size: 0.75rem;
}

.border-dashed {
    border: 2px dashed #e2e8f0 !important;
}
</style>